import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";

export default function BillingSuccess() {
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | pending | failed | expired
  const [planId, setPlanId] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    const MAX_ATTEMPTS = 12;
    const poll = async () => {
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setStatus("pending");
        return;
      }
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        setPlanId(data.plan_id);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        setTimeout(poll, 2500);
      } catch {
        setTimeout(poll, 3000);
      }
    };
    poll();
  }, [location.search, refresh]);

  return (
    <div className="max-w-xl mx-auto pt-10" data-testid="billing-success-page">
      <div className="border border-zinc-200 bg-white p-10 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-blue-600" />
            <h1 className="mt-4 text-2xl font-black tracking-tight">{t("billing_checking")}</h1>
            <p className="mt-2 text-sm text-zinc-500">{t("billing_checking_sub")}</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500" />
            <h1 className="mt-4 text-2xl font-black tracking-tight" data-testid="billing-paid">{t("billing_paid")}</h1>
            <p className="mt-2 text-sm text-zinc-600">{t("billing_paid_sub")} <span className="font-bold uppercase">{planId}</span></p>
            <div className="mt-6 flex justify-center gap-3">
              <Link to="/app" className="btn-accent text-sm font-semibold px-5 py-2.5 rounded-md">{t("billing_to_dashboard")}</Link>
            </div>
          </>
        )}
        {status === "pending" && (
          <>
            <Clock className="w-12 h-12 mx-auto text-amber-500" />
            <h1 className="mt-4 text-2xl font-black tracking-tight">{t("billing_pending")}</h1>
            <p className="mt-2 text-sm text-zinc-500">{t("billing_pending_sub")}</p>
          </>
        )}
        {status === "expired" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-red-500" />
            <h1 className="mt-4 text-2xl font-black tracking-tight">{t("billing_expired")}</h1>
            <button onClick={() => navigate("/app/billing")} className="mt-4 btn-outline text-sm font-semibold px-5 py-2.5 rounded-md">
              {t("billing_back")}
            </button>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-red-500" />
            <h1 className="mt-4 text-2xl font-black tracking-tight">{t("billing_failed")}</h1>
            <button onClick={() => navigate("/app/billing")} className="mt-4 btn-outline text-sm font-semibold px-5 py-2.5 rounded-md">
              {t("billing_back")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
